import { isEmailAddress } from './is-email-address.ts'

/**
 * Creates a filter function to check if an email address is allowed based on a provided list of allowed email addresses.
 * The filter allows exact matches or domain-based matches (e.g., '@example.com' matches any email from that domain).
 *
 * @param {string[]} [allowedEmailAddresses] - An optional array of allowed email addresses or domain-based rules.
 *                                             If a domain is provided, it must start with '@' (e.g., '@example.com').
 *                                             If no addresses are provided, all email addresses are considered allowed.
 * @returns {(emailAddress: string) => boolean} A function that accepts an email address and returns `true` if the email is allowed, otherwise `false`.
 */
export const createAllowedEmailFilter = (allowedEmailAddresses?: string[]) => {
  /**
   * Checks if the provided email address is allowed based on the allowed email addresses or domain rules.
   *
   * @param {string} emailAddress - The email address to be validated.
   * @returns {boolean} `true` if the email address is valid and allowed; `false` otherwise.
   */
  const isAllowedEMailAddress = (emailAddress: string): boolean => {
    // Validate email format using the isEmailAddress function
    if (!isEmailAddress(emailAddress)) {
      return false // Invalid email format, reject
    }

    return (
      !allowedEmailAddresses ||
      allowedEmailAddresses.length === 0 ||
      allowedEmailAddresses.some(
        (allowedEmailAddress) =>
          emailAddress === allowedEmailAddress || // Exact email match
          (allowedEmailAddress.startsWith('@') &&
            emailAddress.endsWith(allowedEmailAddress)) // Domain-based match
      )
    )
  }

  return isAllowedEMailAddress
}
